import chalk from 'chalk';
import * as perf from './performance-logger';
import * as servicesApi from './testimServicesApi';
import * as localRunnerCache from './runnerFileCache';
import * as testimCustomToken from './testimCustomToken';
import { getPublicIp } from '../utils/network';
import { CLI_MODE, REGION_CONFIG } from './constants';
import { getLogger, Logger } from './logger';
import { featureFlagsService } from './featureFlagsService';
import { getPackageVersion } from '../testimNpmDriver';
import { getEnginesVersion } from '../utils';
import { getNpmVersion } from './npmWrapper';
import { setServicesHost } from './config';
import { getRegionFromProjectId, prefixRegionMap } from '../utils/regionUtils';
import { reInitAnalytics } from './testimAnalytics';
import type { RunnerOptions } from '../runOptions';
import type { CommonSharedOptions } from '../runner';

const logger = getLogger('initializeUserWithAuth');

const FIVE_MINUTES_MS = 1000 * 60 * 5;
const TEN_HOURS_MS = 1000 * 60 * 60 * 10;

function preloadSlowRequires(mode?: RunnerOptions['mode']) {
    if (CLI_MODE.EXTENSION === mode) {
        return;
    }
    process.nextTick(() => {
        // heuristic to pay the cost of loading the sessionPlayer here while we are waiting for the backend
        try {
            require('./getSessionPlayerRequire').getSessionPlayer();
        } catch {
            //silent catch
        }
    });
}

function printMessagesFromServer(result: servicesApi.InitializedUserWithAuth) {
    if (result.clientConfig?.clientMessages?.length) {
        result.clientConfig.clientMessages.forEach((message) => {
            const textColor = message.color as typeof chalk.Color || 'yellow';
            const bgColor = message.bgColor as typeof chalk.BackgroundColor || 'bgBlack';
            // eslint-disable-next-line no-console
            console.log(chalk[bgColor](chalk[textColor](message.text)));
        });
    }
}

function setRegionConfig(regionConfig: servicesApi.InitializedUserWithAuth['clientConfig'], projectId: string) {
    const { serviceUrl } = regionConfig;
    setServicesHost(serviceUrl);
    localRunnerCache.memoize(
        async () => regionConfig,
        REGION_CONFIG.CACHE_FN_NAME,
        REGION_CONFIG.TTL,
        { serviceUrl },
    )();
    Logger.setRegion(prefixRegionMap[getRegionFromProjectId(projectId)!]);
}

export async function initializeUserWithAuth(options: CommonSharedOptions) {
    const { project, token, userAccessKey, lightweightMode, useLocalChromeDriver, useChromeLauncher, mode, gridId, grid } = options;

    const lightweightModeGeneral = Boolean(lightweightMode?.general);
    const localGrid = Boolean(useLocalChromeDriver || useChromeLauncher);
    const memoizationTTL = lightweightModeGeneral ? TEN_HOURS_MS : FIVE_MINUTES_MS;
    const clientCliVersion = getPackageVersion();
    const clientNodeVersion = getEnginesVersion();
    const clientNpmVersion = await getNpmVersion();

    let publicIp = null;
    if (featureFlagsService.flags.getPublicIpFrom3rdPartyServices.isEnabled() && !localGrid) {
        try {
            publicIp = await getPublicIp();
        } catch (e) {
            logger.error('failed to get public ip', e);
        }
    }
    let initializedFromCache = true;
    perf.log('before initializeUserWithAuth');
    const result = await localRunnerCache.memoize(async () => {
        preloadSlowRequires(mode);
        initializedFromCache = false;
        return await servicesApi.initializeUserWithAuth({
            projectId: project,
            token,
            branchName: options.branch,
            lightweightMode,
            localGrid,
            publicIp,
            gridId,
            grid,
            clientCliVersion,
            clientNodeVersion,
            clientNpmVersion,
        });
    }, 'initializeUserWithAuth', memoizationTTL, { project, token, branchName: options.branch, lightweightModeGeneral, localGrid, gridId, grid, publicIp })();
    // Printing messages to the user from the server.
    printMessagesFromServer(result);
    setRegionConfig(result.clientConfig, project);
    options.publicIps = [result.clientPublicIp];
    perf.log('after initializeUserWithAuth');
    if (publicIp && result.clientPublicIp && (result.clientPublicIp !== publicIp)) { // There might be situations services don't see our public ip
        logger.error('public ip mismatch', { publicIpFromExternalService: publicIp, publicIpFromServices: result.clientPublicIp });
        options.publicIps.push(publicIp);
    }
    testimCustomToken.initFromData(result.authData, { projectId: project, token, userAccessKey, publicIp: result.clientPublicIp, initializedFromCache });
    reInitAnalytics(result.clientConfig?.segmentKey);
    return result;
}
